(ns iris.scroll.navigation
  (:require
   [iris.anchor-scroll :as anchor-scroll]))

(defonce current-path (atom nil))
(defonce deferred-scroll (atom nil))
(defonce saved-scroll-run-id (atom 0))

(def saved-scroll-options
  {:attempts 80
   :interval-ms 50
   :stable-frames 3
   :min-attempts 20
   :tolerance 4
   :scroll-intent-window-ms 500})

(defn cancel-saved-scroll! []
  (swap! saved-scroll-run-id inc))

(defn begin-navigation! []
  (cancel-saved-scroll!)
  (reset! deferred-scroll nil))

(defn absolute-url [url]
  (js/URL. url (.. js/window -location -origin)))

(defn path-from-url [url]
  (.-pathname (absolute-url url)))

(defn fragment-from-url [url]
  (let [hash (.-hash (absolute-url url))]
    (when (seq hash)
      (subs hash 1))))

(defn same-path? [url]
  (= (path-from-url url)
     (or @current-path
         (.. js/window -location -pathname))))

(defn scroll-to-top! []
  (.scrollTo js/window #js {:top 0 :behavior "auto"}))

(defn max-scroll-top []
  (max 0
       (- (.. js/document -documentElement -scrollHeight)
          (.-innerHeight js/window))))

(defn document-layout []
  {:scroll-height (.. js/document -documentElement -scrollHeight)
   :max-scroll-top (max-scroll-top)})

(defn same-layout? [a b]
  (and a b
       (= (:scroll-height a) (:scroll-height b))
       (= (:max-scroll-top a) (:max-scroll-top b))))

(defn scroll-away-from-saved? [target tolerance]
  (> (js/Math.abs (- (.-pageYOffset js/window) target))
     tolerance))

(defn scroll-to-saved!
  ([scroll-top] (scroll-to-saved! scroll-top nil))
  ([scroll-top options]
   (let [{:keys [attempts interval-ms stable-frames min-attempts tolerance
                 scroll-intent-window-ms]}
         (merge saved-scroll-options options)
         target (max 0 scroll-top)
         started-at (.now js/Date)
         run-id (swap! saved-scroll-run-id inc)]
     (anchor-scroll/attach-user-scroll-intent-listeners!)
     (letfn [(current-run? []
               (= run-id @saved-scroll-run-id))
             (target-reachable? []
               (<= target (+ (max-scroll-top) tolerance)))
             (user-cancelled? [has-scrolled?]
               (and has-scrolled?
                    (scroll-away-from-saved? target tolerance)
                    (anchor-scroll/recent-user-scroll-intent?
                     started-at
                     scroll-intent-window-ms)))
             (schedule! [attempts-left attempt-count stable-count previous-layout has-scrolled?]
               (js/setTimeout
                #(attempt! attempts-left
                           attempt-count
                           stable-count
                           previous-layout
                           has-scrolled?)
                interval-ms))
             (attempt! [attempts-left attempt-count stable-count previous-layout has-scrolled?]
               (.requestAnimationFrame
                js/window
                (fn []
                  (when (and (current-run?)
                             (pos? attempts-left)
                             (not (user-cancelled? has-scrolled?)))
                    (let [layout (document-layout)
                          stable-count (if (same-layout? previous-layout layout)
                                         (inc stable-count)
                                         0)
                          attempt-count (inc attempt-count)
                          reachable? (target-reachable?)
                          has-scrolled? (or has-scrolled? reachable?)]
                      (when reachable?
                        (.scrollTo js/window #js {:top target :behavior "auto"}))
                      (when (or (not reachable?)
                                (< attempt-count min-attempts)
                                (< stable-count stable-frames))
                        (schedule! (dec attempts-left)
                                   attempt-count
                                   stable-count
                                   layout
                                   has-scrolled?)))))))]
       (attempt! attempts 0 0 nil false)))))

(defn defer-scroll! [f]
  (reset! deferred-scroll f))

(defn flush-deferred-scroll! []
  (when-let [f @deferred-scroll]
    (reset! deferred-scroll nil)
    (f)))

(defn after-render! []
  (flush-deferred-scroll!))

(defn navigate-page!
  [{:keys [url saved-scroll-top post-owned-anchor?]}]
  (let [same-path? (same-path? url)
        path (path-from-url url)
        fragment (fragment-from-url url)]
    (begin-navigation!)
    (reset! current-path path)
    (cond
      (some? saved-scroll-top)
      (if same-path?
        (scroll-to-saved! saved-scroll-top)
        (defer-scroll! #(scroll-to-saved! saved-scroll-top)))

      ;; Post-rendered anchors are data-aware: posts/views.cljs scrolls them after
      ;; post data and DOM targets render. On cross-page links, move away from
      ;; the previous page's scroll position while that data-aware scroll waits.
      post-owned-anchor?
      (when-not same-path?
        (defer-scroll! scroll-to-top!))

      (seq fragment)
      (defer-scroll! #(anchor-scroll/scroll-to-anchor! fragment))

      same-path?
      (scroll-to-top!)

      :else
      (defer-scroll! scroll-to-top!))))
